import { fromCallback } from 'xstate';

import { EntitiesMapping, FlowEntity } from '../types';

import { clearEmailCheckpointOnNavigation } from './ptvSisu/emailSession';
import {
    accountFlow,
    editEmailActor,
    editPasswordActor,
    optInActor,
    submitFormChangePasswordActor,
    submitFormUpdateEmailActor,
} from './account';
import { activateFlow, submitFormActivateActor } from './activate';
import { activateDeviceFlow } from './activateDevice';
import { checkEmailFlow, submitFormCheckEmailActor } from './checkEmail';
import { fetchCmpConsentTokenActor } from './consent';
import { directFlow } from './direct';
import { dtcFlow } from './dtc';
import { forgotPasswordActor, ForgotPasswordFlow } from './forgotPassword';
import { removeFlowIdFromSession, ROUTE_CHANGE } from './helpers';
import { invalidCouponFlow } from './invalidCoupon';
import { mvpdBindActor, mvpdFlow } from './mvpd';
import { partnerBindActor } from './partner';
import { ptvSisuFlow } from './ptvSisu';
import {
    productRetrievalActor,
    redeemCouponFlow,
    submitRedeemCouponActor,
    validationCouponActor,
} from './redeemCoupon';
import { rendezvousFlow } from './rendezvous';
import { signInFlow, submitFormSignInActor } from './signin';
import { signUpFlow, submitFormSignUpActor } from './signup';
import { submitFormTvActor, tvFlow } from './tv';
import { tvCompleteFlow } from './tvComplete';
import { tvSelectFlow } from './tvSelect';
import { updateEmailActor } from './updateEmail';
import { resolveUpsellDocumentsActor, upsellFlow } from './upsell';

const navigationMachine: FlowEntity = {
    initActor: ({ router, getConfigByKey, region }) =>
        fromCallback(({ sendBack }) => {
            const handlePopState = (event: PopStateEvent) => {
                if (event.state?.url) {
                    clearEmailCheckpointOnNavigation(event.state.url);
                    removeFlowIdFromSession(event.state.url, getConfigByKey, region);
                }
                sendBack({ type: 'POP_STATE', payload: event });
            };

            const handleNextNavigation = (url: string) => {
                clearEmailCheckpointOnNavigation(url);
                removeFlowIdFromSession(url, getConfigByKey, region);
            };
            // After the URL moved: the route guards re-read it, as they do after popstate.
            const handleNextNavigationComplete = () => sendBack({ type: ROUTE_CHANGE });

            window.addEventListener('popstate', handlePopState);
            router.events.on('routeChangeStart', handleNextNavigation);
            router.events.on('routeChangeComplete', handleNextNavigationComplete);

            return () => {
                window.removeEventListener('popstate', handlePopState);
                router.events.off('routeChangeStart', handleNextNavigation);
                router.events.off('routeChangeComplete', handleNextNavigationComplete);
            };
        }),
};

export const flowEntityMap: EntitiesMapping = {
    dtc: {
        entity: dtcFlow,
        subEntities: {
            upsell: {
                entity: upsellFlow,
                subEntities: { resolveDocuments: { entity: resolveUpsellDocumentsActor } },
            },
            mvpd: { entity: mvpdFlow },
            mvpdBind: { entity: mvpdBindActor },
            partnerBind: { entity: partnerBindActor },
            forgotPassword: { entity: ForgotPasswordFlow },
            checkEmail: {
                entity: checkEmailFlow,
                subEntities: { submitting: { entity: submitFormCheckEmailActor } },
            },
            signIn: {
                entity: signInFlow,
                subEntities: {
                    submitting: { entity: submitFormSignInActor },
                    fetchCmpConsentToken: { entity: fetchCmpConsentTokenActor },
                },
            },
            signUp: {
                entity: signUpFlow,
                subEntities: {
                    submitting: { entity: submitFormSignUpActor },
                },
            },
            nativeBrowserNavigation: { entity: navigationMachine },
        },
    },
    rendezvous: {
        entity: rendezvousFlow,
        subEntities: {
            tv: { entity: tvFlow, subEntities: { submitting: { entity: submitFormTvActor } } },
            tvSelect: { entity: tvSelectFlow },
            tvComplete: {
                entity: tvCompleteFlow,
                subEntities: { submitting: { entity: submitFormTvActor } },
            },
            nativeBrowserNavigation: { entity: navigationMachine },
        },
    },
    direct: {
        entity: directFlow,
        subEntities: {
            redeemCoupon: {
                entity: redeemCouponFlow,
                subEntities: {
                    productRetrieval: { entity: productRetrievalActor },
                    validationCoupon: { entity: validationCouponActor },
                    submitting: { entity: submitRedeemCouponActor },
                    invalidCoupon: {
                        entity: invalidCouponFlow,
                    },
                },
            },
            nativeBrowserNavigation: { entity: navigationMachine },
        },
    },
    ptvSisu: {
        entity: ptvSisuFlow,
        subEntities: {
            checkEmail: {
                entity: checkEmailFlow,
                subEntities: { submitting: { entity: submitFormCheckEmailActor } },
            },
            signIn: {
                entity: signInFlow,
                subEntities: {
                    submitting: { entity: submitFormSignInActor },
                    forgotPassword: { entity: forgotPasswordActor },
                    fetchCmpConsentToken: { entity: fetchCmpConsentTokenActor },
                },
            },
            signUp: {
                entity: signUpFlow,
                subEntities: {
                    submitting: { entity: submitFormSignUpActor },
                    fetchCmpConsentToken: { entity: fetchCmpConsentTokenActor },
                },
            },
            nativeBrowserNavigation: { entity: navigationMachine },
        },
    },
    activateDevice: {
        entity: activateDeviceFlow,
        subEntities: {
            checkEmail: {
                entity: checkEmailFlow,
                subEntities: { submitting: { entity: submitFormCheckEmailActor } },
            },
            signIn: {
                entity: signInFlow,
                subEntities: {
                    submitting: { entity: submitFormSignInActor },
                    forgotPassword: { entity: forgotPasswordActor },
                    fetchCmpConsentToken: { entity: fetchCmpConsentTokenActor },
                },
            },
            activate: {
                entity: activateFlow,
                subEntities: { submitting: { entity: submitFormActivateActor } },
            },
            signUp: {
                entity: signUpFlow,
                subEntities: {
                    submitting: { entity: submitFormSignUpActor },
                    fetchCmpConsentToken: { entity: fetchCmpConsentTokenActor },
                },
            },
            confirmAccount: { entity: navigationMachine },
            nativeBrowserNavigation: { entity: navigationMachine },
        },
    },
    account: {
        entity: accountFlow,
        subEntities: {
            editEmail: { entity: editEmailActor },
            editPassword: { entity: editPasswordActor },
            optInToggled: { entity: optInActor },
            updateEmail: { entity: updateEmailActor },
            updatingEmail: { entity: submitFormUpdateEmailActor },
            submitting: { entity: submitFormChangePasswordActor },
            nativeBrowserNavigation: { entity: navigationMachine },
        },
    },
};
